import clsx from 'clsx';
import { format } from 'date-fns';
import { ArrowRight, MoreHorizontal, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TreatmentCheckInV2Status } from '@/services/api/treatmentCheckInV2Api';
import { useDeleteCheckInV2Mutation } from '@/services/api/treatmentCheckInV2Api';
import { useDeleteTreatmentCheckInMutation } from '@/services/api/treatmentService';
import {
  TreatmentCheckInStatus,
} from '@/services/types/treatment-types';

import { CheckInAuthor } from './CheckInAuthor';
import { CheckInStatusBadge } from './CheckInStatusBadge';
import { CheckInStatusIcon } from './CheckInStatusIcon';
import { DeleteCheckInConfirmationDialog } from './DeleteCheckInConfirmationDialog';
import type { UnifiedCheckIn } from './TreatmentCheckInsPage';

// ── V2 Status Helpers ────────────────────────────────────────────────────────

function getV2StatusBadge(status: TreatmentCheckInV2Status): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'FINALIZED':
      return { label: 'Finalized', className: 'bg-emerald-100 text-emerald-700' };
    case 'IN_REVIEW':
      return { label: 'In Review', className: 'bg-purple-100 text-purple-700' };
    case 'GENERATING':
      return { label: 'Generating', className: 'bg-blue-100 text-blue-700' };
    case 'NOTES_TAKEN':
      return { label: 'Notes Taken', className: 'bg-sky-100 text-sky-700' };
    case 'IN_CALL':
      return { label: 'In Call', className: 'bg-orange-100 text-orange-700' };
    case 'NO_ANSWER':
      return { label: 'No Answer', className: 'bg-gray-100 text-gray-600' };
    case 'LEFT_VOICEMAIL':
      return { label: 'Left VM', className: 'bg-gray-100 text-gray-600' };
    case 'LEFT_TEXT':
      return { label: 'Left Text', className: 'bg-gray-100 text-gray-600' };
    case 'DRAFT':
    default:
      return { label: 'Draft', className: 'bg-gray-100 text-gray-600' };
  }
}

function callOutcomeBadge(outcome: string | null): {
  label: string;
  className: string;
} | null {
  switch (outcome) {
    case 'CONNECTED':
      return { label: 'Connected', className: 'bg-green-100 text-green-700' };
    case 'NO_ANSWER':
      return { label: 'No Answer', className: 'bg-gray-100 text-gray-600' };
    case 'LEFT_VOICEMAIL':
      return { label: 'Left VM', className: 'bg-amber-100 text-amber-700' };
    case 'LEFT_TEXT':
      return { label: 'Left Text', className: 'bg-amber-100 text-amber-700' };
    default:
      return null;
  }
}

/** V2 statuses that indicate the check-in is still active / in progress */
const V2_ACTIVE_STATUSES = new Set<TreatmentCheckInV2Status>([
  'DRAFT',
  'IN_CALL',
  'NOTES_TAKEN',
  'GENERATING',
  'IN_REVIEW',
]);

// ── Component ────────────────────────────────────────────────────────────────

interface TreatmentCheckInCardProps {
  matterId: string;
  item: UnifiedCheckIn;
}

export const TreatmentCheckInCard = ({
  matterId,
  item,
}: TreatmentCheckInCardProps) => {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteCheckIn, { isLoading: isDeleting }] =
    useDeleteTreatmentCheckInMutation();
  const [isV2DeleteDialogOpen, setIsV2DeleteDialogOpen] = useState(false);
  const [deleteCheckInV2, { isLoading: isDeletingV2 }] =
    useDeleteCheckInV2Mutation();

  // ── V1 card ──────────────────────────────────────────────────────────────

  if (item.version === 'v1') {
    const { checkIn } = item;
    const dateStr = checkIn.check_in_time || checkIn.created_at;
    const formattedDate = dateStr
      ? format(new Date(dateStr), 'PP')
      : 'Unknown date';
    const isDraft = checkIn.status === TreatmentCheckInStatus.DRAFT;

    const handleCardClick = () => {
      if (isDraft) {
        navigate(
          `/matters/${matterId}/treatment/check-ins/${checkIn.id}/edit`,
        );
      } else {
        navigate(`/matters/${matterId}/treatment/check-ins/${checkIn.id}`);
      }
    };

    const handleEditClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(
        `/matters/${matterId}/treatment/check-ins/${checkIn.id}/edit`,
      );
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
      try {
        await deleteCheckIn({ matterId, checkInId: checkIn.id }).unwrap();
        toast.success('Treatment check-in deleted successfully.');
        setIsDeleteDialogOpen(false);
      } catch {
        toast.error('Failed to delete treatment check-in.');
      }
    };

    return (
      <>
        <div
          role="button"
          tabIndex={0}
          onClick={handleCardClick}
          onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
          className="w-full text-left bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3 flex items-center justify-between hover:shadow transition-shadow cursor-pointer"
        >
          <div className="flex items-center gap-3 overflow-hidden">
            <span className="flex-shrink-0 text-gray-400">
              <CheckInStatusIcon status={checkIn.status} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                Check-in on {formattedDate}
              </p>
              {checkIn.user_id && <CheckInAuthor userId={checkIn.user_id} />}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <CheckInStatusBadge status={checkIn.status} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="More actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                {isDraft && (
                  <DropdownMenuItem onClick={handleEditClick}>
                    <Pencil className="mr-2 h-4 w-4" />
                    <span>Edit</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="text-red-500 focus:bg-red-50 focus:text-red-500"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ArrowRight className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        <DeleteCheckInConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />
      </>
    );
  }

  // ── V2 card ──────────────────────────────────────────────────────────────

  const { checkIn } = item;
  const formattedDate = checkIn.created_at
    ? format(new Date(checkIn.created_at), 'PP')
    : 'Unknown date';

  const statusBadge = getV2StatusBadge(checkIn.status);
  const outcomeBadge = callOutcomeBadge(checkIn.call_outcome);
  const isActive = V2_ACTIVE_STATUSES.has(checkIn.status);

  const handleV2CardClick = () => {
    if (isActive) {
      // Active check-in → go to live/operator view
      navigate(`/matters/${matterId}/treatment/check-in-v2/${checkIn.id}`);
    } else {
      // Terminal status → read-only detail view
      navigate(
        `/matters/${matterId}/treatment/check-in-v2/${checkIn.id}/details`,
      );
    }
  };

  const handleV2DeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsV2DeleteDialogOpen(true);
  };

  const handleV2DeleteConfirm = async () => {
    try {
      await deleteCheckInV2({ matterId, checkInId: checkIn.id }).unwrap();
      toast.success('Smart check-in deleted successfully.');
      setIsV2DeleteDialogOpen(false);
    } catch {
      toast.error('Failed to delete smart check-in.');
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleV2CardClick}
        onKeyDown={(e) => e.key === 'Enter' && handleV2CardClick()}
        className="w-full text-left bg-white border border-purple-200 rounded-lg shadow-sm px-4 py-3 flex items-center justify-between hover:shadow transition-shadow cursor-pointer"
      >
        <div className="flex items-center gap-3 overflow-hidden">
          <span className="flex-shrink-0 text-purple-500">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              Smart Check-in on {formattedDate}
            </p>
            {checkIn.created_by_id && (
              <CheckInAuthor userId={checkIn.created_by_id} />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {outcomeBadge && (
            <span
              className={clsx(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                outcomeBadge.className,
              )}
            >
              {outcomeBadge.label}
            </span>
          )}
          <span
            className={clsx(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              statusBadge.className,
            )}
          >
            {statusBadge.label}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => e.stopPropagation()}
                aria-label="More actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={handleV2DeleteClick}
                disabled={isDeletingV2}
                className="text-red-500 focus:bg-red-50 focus:text-red-500"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ArrowRight className="h-4 w-4 text-gray-400" />
        </div>
      </div>
      <DeleteCheckInConfirmationDialog
        isOpen={isV2DeleteDialogOpen}
        onOpenChange={setIsV2DeleteDialogOpen}
        onConfirm={handleV2DeleteConfirm}
        isDeleting={isDeletingV2}
      />
    </>
  );
};
